import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users as UsersIcon, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import ConfirmDialog from '../components/ConfirmDialog';

const OrbitPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewContact, setShowNewContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  useEffect(() => {
    fetchContacts();
  }, []);
  
  const fetchContacts = async () => {
    try {
      const data = await apiFetch('/api/orbit');
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error(error?.message || 'Could not load contacts');
    } finally {
      setLoading(false);
    }
  };
  
  const performDeleteContact = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    try {
      await apiFetch(`/api/orbit/${id}`, { method: 'DELETE' });
      toast.success('Contact removed');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error(error?.message || 'Failed to delete contact');
      throw error;
    }
  };
  
  const getContactHealth = (lastContacted) => {
    if (!lastContacted) return 'red';
    const daysSince = Math.floor((Date.now() - new Date(lastContacted)) / (1000 * 60 * 60 * 24));
    if (daysSince <= 30) return 'green';
    if (daysSince <= 60) return 'yellow';
    return 'red';
  };
  
  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="orbit-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold text-text-primary mb-2">Your Orbit</h1>
            <p className="text-text-secondary">The people who matter to your journey</p>
          </div>
          <button
            onClick={() => setShowNewContact(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all shadow-sm"
            data-testid="new-contact-button"
          >
            <Plus size={20} />
            Add Contact
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-2xl border border-border">
            <div className="w-16 h-16 bg-teal-light rounded-full flex items-center justify-center mx-auto mb-4">
              <UsersIcon className="text-teal" size={32} />
            </div>
            <h3 className="font-display text-xl font-bold text-text-primary mb-2">No contacts yet</h3>
            <p className="text-text-secondary mb-6">Start building your network</p>
            <button
              onClick={() => setShowNewContact(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all"
            >
              <Plus size={20} />
              Add First Contact
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contacts.map((contact) => {
              const health = getContactHealth(contact.last_contacted);
              const healthColors = {
                green: 'bg-sage',
                yellow: 'bg-amber',
                red: 'bg-error'
              };
              
              return (
                <div key={contact.id} className="bg-bg-surface rounded-2xl p-6 border border-border card-hover">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-teal-light rounded-full flex items-center justify-center font-semibold text-teal">
                        {contact.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-bold text-text-primary">{contact.name}</h3>
                        <span className="text-xs text-text-tertiary capitalize">{contact.relevance}</span>
                        {contact.connected_user_id && (
                          <span className="ml-2 px-2 py-0.5 bg-gold-light text-gold text-xs rounded-full">Daypop</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${healthColors[health]}`} title={health} />
                    </div>
                  </div>
                  
                  {contact.discussion && (
                    <p className="text-sm text-text-secondary mb-3 line-clamp-2">{contact.discussion}</p>
                  )}
                  
                  {/* Contact Info */}
                  {(contact.email || contact.phone || contact.linkedin) && (
                    <div className="text-xs text-text-tertiary space-y-1 mb-3">
                      {contact.email && <div>📧 {contact.email}</div>}
                      {contact.phone && <div>📱 {contact.phone}</div>}
                      {contact.linkedin && <div>💼 LinkedIn</div>}
                    </div>
                  )}
                  
                  {contact.follow_up_date && (
                    <div className="text-xs text-text-tertiary mb-3">
                      Follow up: {new Date(contact.follow_up_date).toLocaleDateString()}
                    </div>
                  )}
                  
                  {contact.tags && contact.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap mb-3">
                      {contact.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-teal-light text-teal text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-3 border-t border-border">
                    <button
                      onClick={() => setEditingContact(contact)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-gold hover:bg-gold-light rounded-lg transition-all"
                      data-testid={`edit-contact-${contact.id}`}
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ id: contact.id, name: contact.name })}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/10 rounded-lg transition-all"
                      data-testid={`delete-contact-${contact.id}`}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {showNewContact && <ContactModal onClose={() => setShowNewContact(false)} onSave={fetchContacts} />}
        {editingContact && <ContactModal contact={editingContact} onClose={() => setEditingContact(null)} onSave={fetchContacts} />}

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title="Remove this contact?"
          message="They will be removed from your orbit. This won't notify them, and you can't undo this."
          detail={deleteTarget?.name ? deleteTarget.name : null}
          confirmLabel="Remove contact"
          onConfirm={performDeleteContact}
        />
      </div>
    </div>
  );
};

const ContactModal = ({ contact = null, onClose, onSave }) => {
  const isEditing = !!contact;
  const [formData, setFormData] = useState(contact ? {
    name: contact.name,
    where_met: contact.where_met || '',
    relevance: contact.relevance,
    discussion: contact.discussion || '',
    what_they_offer: contact.what_they_offer || '',
    what_i_can_do: contact.what_i_can_do || '',
    follow_up_date: contact.follow_up_date || '',
    follow_up_note: contact.follow_up_note || '',
    tags: contact.tags || [],
    email: contact.email || '',
    linkedin: contact.linkedin || '',
    phone: contact.phone || '',
    connected_user_id: contact.connected_user_id || null
  } : {
    name: '',
    where_met: '',
    relevance: 'other',
    discussion: '',
    what_they_offer: '',
    what_i_can_do: '',
    follow_up_date: '',
    follow_up_note: '',
    tags: [],
    email: '',
    linkedin: '',
    phone: '',
    connected_user_id: null
  });
  const [saving, setSaving] = useState(false);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  const handleSearchUser = async () => {
    if (searchUsername.length < 2) {
      toast.error('Enter at least 2 characters to search');
      return;
    }
    
    setSearching(true);
    try {
      const data = await apiFetch(
        `/api/users/search?q=${encodeURIComponent(searchUsername)}`
      );
      const users = data.users || [];
      setSearchResults(users);
      if (users.length === 0) {
        toast.info('No Daypop users found with that username');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error(error?.message || 'Failed to search users');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSelectUser = (user) => {
    const raw = user.id != null ? user.id : user.user_id;
    const otherId = raw != null && raw !== '' ? String(raw).trim() : '';
    if (!otherId) {
      toast.error('That search result is missing a user id. Try searching again.');
      return;
    }
    setFormData({
      ...formData,
      name: user.name,
      connected_user_id: otherId,
      relevance: 'collaborator',
      discussion: `Connected via Daypop (@${user.username})`
    });
    setSearchResults([]);
    setSearchUsername('');
    toast.success(`Selected Daypop user: ${user.name}`);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const path = isEditing ? `/api/orbit/${contact.id}` : '/api/orbit';
      const data = await apiFetch(path, {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify(formData),
      });
      if (!isEditing && data?.reverse_link_created === false) {
        toast.success('Contact added. Two-way link will retry later.');
      } else {
        toast.success(isEditing ? 'Contact updated successfully!' : 'Contact added successfully!');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(error?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-overlay-in" onClick={onClose}>
      <div className="bg-bg-surface rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-modal-in custom-scrollbar" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-3xl font-bold text-text-primary mb-6">
          {isEditing ? 'Edit Contact' : 'Add Contact'}
        </h2>
        
        {/* Search Daypop User */}
        {!isEditing && !formData.connected_user_id && (
          <div className="mb-6 p-4 bg-gold-light/30 rounded-xl border border-gold/30">
            <label className="block text-sm font-medium text-text-primary mb-2">
              🔍 Search Daypop User (Optional)
            </label>
            <p className="text-xs text-text-secondary mb-3">If this person is on Daypop, search by their username to connect</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchUsername}
                onChange={(e) => setSearchUsername(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchUser()}
                placeholder="Enter username..."
                className="flex-1 px-4 py-2 bg-bg-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                data-testid="username-search-input"
              />
              <button
                type="button"
                onClick={handleSearchUser}
                disabled={searching}
                className="px-4 py-2 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary text-white rounded-lg transition-all"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                {searchResults.map((user) => (
                  <div
                    key={user.id || user.user_id}
                    onClick={() => handleSelectUser(user)}
                    className="flex items-center gap-3 p-3 bg-bg-surface border border-border hover:border-gold rounded-lg cursor-pointer transition-all"
                  >
                    <div className="w-10 h-10 bg-teal-light rounded-full flex items-center justify-center font-semibold text-teal text-sm shrink-0">
                      {(user.name || '?')
                        .split(/\s+/)
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-text-primary truncate">{user.name}</div>
                      <div className="text-xs text-text-secondary">@{user.username}</div>
                    </div>
                    {user.username ? (
                      <Link
                        to={`/u/${encodeURIComponent(user.username)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-medium text-gold hover:text-gold-muted shrink-0"
                      >
                        View profile
                      </Link>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {formData.connected_user_id && (
          <div className="mb-4 p-3 bg-gold-light rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gold">✓ Daypop User Selected</span>
            </div>
            {!isEditing && (
              <button
                type="button"
                onClick={() => setFormData({ ...formData, connected_user_id: null, name: '', discussion: '' })}
                className="text-xs text-text-secondary hover:text-error"
              >
                Clear
              </button>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={!!formData.connected_user_id}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              data-testid="contact-name-input"
            />
          </div>
          
          {/* Contact Information Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">📧 Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                data-testid="contact-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">📱 Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 234 567 8900"
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                data-testid="contact-phone-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">💼 LinkedIn</label>
              <input
                type="url"
                value={formData.linkedin}
                onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                placeholder="linkedin.com/in/..."
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                data-testid="contact-linkedin-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Where you met</label>
              <input
                type="text"
                value={formData.where_met}
                onChange={(e) => setFormData({ ...formData, where_met: e.target.value })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Relevance</label>
              <select
                value={formData.relevance}
                onChange={(e) => setFormData({ ...formData, relevance: e.target.value })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="investor">Investor</option>
                <option value="mentor">Mentor</option>
                <option value="client">Client</option>
                <option value="collaborator">Collaborator</option>
                <option value="interesting">Interesting Stranger</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">What you discussed</label>
            <textarea
              value={formData.discussion}
              onChange={(e) => setFormData({ ...formData, discussion: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
            />
          </div>
          
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-border text-text-secondary rounded-xl hover:border-gold transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name}
              className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all"
              data-testid="save-contact-button"
            >
              {saving ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Contact' : 'Add Contact')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrbitPage;
